import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
  Badge,
  Accordion,
  InputGroup,
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaRoad,
  FaRoute,
  FaTruck,
  FaTimes,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import "bootstrap/dist/css/bootstrap.min.css";
import car from "../../assets/images/c2.jpg";
import caddyImg from "../../assets/images/Caddy.png";
import busImg from "../../assets/images/Bus.png";
import maxiBusImg from "../../assets/images/MaxiBus.png";
import planenbusImg from "../../assets/images/Planenbus.png";
import "./BookingPage.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { useNavigate } from "react-router-dom";
import { postalAreaAPI, userAPI } from "../../api/apiSystem";
import LocationAutocomplete from "../LocationAutocomplete";
import { useTranslation } from "react-i18next";
import { translateBackendMessage } from "../../api/apiSystem";
import ClientChatBox from "../chat/ClientChatBox";
import { MdDelete } from "react-icons/md";
import { MultiLocationMap } from "../MultiLocationMap";
import SEO from "../SEO";
import { FaLocationDot } from "react-icons/fa6";

// Array of vehicle images
const vehicleImages = [caddyImg, busImg, maxiBusImg, planenbusImg];

export default function BookingPage() {
  const { t } = useTranslation();
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [zustellungFields, setZustellungFields] = useState([""]);
  const [abholungFields, setAbholungFields] = useState([""]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingVehicles, setFetchingVehicles] = useState(true);
  const [calculatedData, setCalculatedData] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState([]);
  const [needsHebebuehne, setNeedsHebebuehne] = useState(null);
  const [locationsChanged, setLocationsChanged] = useState(0);

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const navigate = useNavigate();
  const { handleSubmit } = useForm();

  const [formData, setFormData] = useState({
    abholung: [{ address: "", lat: null, lng: null, order: 0 }],
    zustellung: [{ address: "", lat: null, lng: null, order: 1 }],
  });

  // ✅ عداد بيدي رقم ترتيب متزايد لكل محطة (Pickup أو Drop) بالظبط زي ما
  // العميل بيضيفها، عشان لما نبعت للباك إند نحافظ على نفس ترتيب الإدخال
  // الحقيقي، مش بس ترتيب افتراضي (pickup, drop, pickup, drop...).
  const orderCounterRef = useRef(2);

  const [extraServices, setExtraServices] = useState({
    loading: false,
    unloading: false,
  });

  // إضافة state لتتبع الإحداثيات من الـ response
  const [coordinates, setCoordinates] = useState({
    from: null,
    to: null,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const savedBooking = localStorage.getItem("bookingData");
    if (savedBooking) {
      try {
        const parsed = JSON.parse(savedBooking);

        let abholungData = [];
        let zustellungData = [];
        let nextOrder = 0;

        if (parsed.stops && Array.isArray(parsed.stops)) {
          parsed.stops.forEach((s) => {
            const entry = {
              address: s.address || "",
              lat: s.lat || null,
              lng: s.lng || null,
              order: nextOrder++,
            };
            if (s.type === "dropoff" || s.type === "drop") {
              zustellungData.push(entry);
            } else {
              abholungData.push(entry);
            }
          });
        } else {
          if (parsed.pickups && Array.isArray(parsed.pickups)) {
            abholungData = parsed.pickups.map((item) => ({
              address: item.address || item,
              lat: item.lat || null,
              lng: item.lng || null,
            }));
          } else if (parsed.booking_from) {
            abholungData = Array.isArray(parsed.booking_from)
              ? parsed.booking_from.map((addr) => ({ address: addr }))
              : [{ address: parsed.booking_from }];
          }

          if (parsed.deliveries && Array.isArray(parsed.deliveries)) {
            zustellungData = parsed.deliveries.map((item) => ({
              address: item.address || item,
              lat: item.lat || null,
              lng: item.lng || null,
            }));
          } else if (parsed.booking_to) {
            zustellungData = Array.isArray(parsed.booking_to)
              ? parsed.booking_to.map((addr) => ({ address: addr }))
              : [{ address: parsed.booking_to }];
          }

          abholungData = abholungData.map((item, i) => ({
            ...item,
            order: i * 2,
          }));
          zustellungData = zustellungData.map((item, i) => ({
            ...item,
            order: i * 2 + 1,
          }));
        }

        const maxOrder = [...abholungData, ...zustellungData].reduce(
          (max, item) => Math.max(max, item.order ?? 0),
          -1,
        );
        orderCounterRef.current = maxOrder + 1;

        setFormData({
          abholung: abholungData,
          zustellung: zustellungData,
        });

        setAbholungFields(abholungData.map(() => ""));
        setZustellungFields(zustellungData.map(() => ""));

        if (parsed.vehicle_id) {
          setSelectedVehicle(Number(parsed.vehicle_id));
        }

        console.log("📦 Loaded multi-location booking data:", { abholungData, zustellungData });
      } catch (error) {
        console.error("❌ Error parsing booking data:", error);
      }
    }

    async function fetchPostalAreas() {
      try {
        const res = await postalAreaAPI.index();
        const vehiclesData = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];

        console.log("🚚 Fetched vehicles data:", vehiclesData);
        setVehicles(vehiclesData);
      } catch (err) {
        console.error("❌ Error loading vehicles:", err);
      } finally {
        setFetchingVehicles(false);
      }
    }

    fetchPostalAreas();
    AOS.init({ duration: 1000, once: true });
  }, []);

  useEffect(() => {
    if (selectedVehicle !== 4) {
      setNeedsHebebuehne(null);
    }
  }, [selectedVehicle]);

  useEffect(() => {
    if (selectedVehicle === 4 && needsHebebuehne !== null) {
      setLocationsChanged((prev) => prev + 1);
    }
  }, [needsHebebuehne]);

  // ==================== حساب السعر ====================
  // يتم تنفيذ هذا الـ useEffect فقط عند تغيير:
  // - السيارة المختارة
  // - الحاجة إلى رافعة (للشاحنة 4)
  // - عدد أو محتوى المواقع (عند الإضافة/الحذف/الاختيار من القائمة)
  // نستخدم locationsChanged كـ trigger إضافي للحساب بعد اختيار موقع
  useEffect(() => {
    const calculatePrice = async () => {
      // ✅ التحقق من أن جميع العناوين تحتوي على إحداثيات (lat, lng) صالحة
      const allPickupsHaveCoords = formData.abholung.every(
        (item) => item.lat !== null && item.lng !== null && item.lat !== undefined && item.lng !== undefined
      );
      const allDeliveriesHaveCoords = formData.zustellung.every(
        (item) => item.lat !== null && item.lng !== null && item.lat !== undefined && item.lng !== undefined
      );

      // يجب أن تحتوي جميع العناوين على إحداثيات (أي تم اختيارها من القائمة)
      if (!allPickupsHaveCoords || !allDeliveriesHaveCoords) {
        // إذا لم تكن جميع المواقع مختارة، لا نحسب السعر
        setPriceData(null);
        setError("");
        setCoordinates({ from: null, to: null });
        return;
      }

      const hasValidAddresses =
        formData.abholung.some((item) => item?.address?.trim()) &&
        formData.zustellung.some((item) => item?.address?.trim());

      if (!hasValidAddresses) {
        setPriceData(null);
        setError("");
        setCoordinates({ from: null, to: null });
        return;
      }

      setCalculatingPrice(true);
      setError("");
      try {
        const stops = getOrderedStops();
        const payload = {
          vehicle_id: selectedVehicle,
          stops,
          booking_from: stops
            .filter((s) => s.type === "pickup")
            .map((s) => s.address),
          booking_to: stops
            .filter((s) => s.type === "dropoff")
            .map((s) => s.address),
          crane: selectedVehicle === 4 ? (needsHebebuehne ? "yes" : "no") : "no",
        };

        if (payload.booking_from.length === 0 || payload.booking_to.length === 0) {
          setPriceData(null);
          setError("");
          setCoordinates({ from: null, to: null });
          return;
        }

        const res = await userAPI.bookings.calculateVehicles(payload);
        console.log("💰 API Response:", res);

        if (res.success) {
          setPriceData(res.data?.data || null);
          setError("");
          console.log("💰 Updated price data:", res.data?.data);

          // تحديث الإحداثيات من الـ response
          updateCoordinatesFromResponse(res.data?.data);
        } else {
          setError(res.message || t("error_calculating_price"));
          setPriceData(null);
          setCoordinates({ from: null, to: null });
        }
      } catch (error) {
        console.error("❌ Error calculating price:", error);
        setError(t("error_calculating_price"));
        setPriceData(null);
        setCoordinates({ from: null, to: null });
      } finally {
        setCalculatingPrice(false);
      }
    };

    // تأخير بسيط لتجنب الحساب أثناء التحديثات المتتالية
    const timeoutId = setTimeout(calculatePrice, 500);
    return () => clearTimeout(timeoutId);
  }, [
    selectedVehicle,
    needsHebebuehne,
    locationsChanged,
    // نضيف formData.abholung و formData.zustellung كـ dependencies لضمان التحديث عند تغيير الإحداثيات
    formData.abholung,
    formData.zustellung,
    t,
  ]);

  /* ============================================
   Vehicle Card - Disabled State
   ============================================ */
  const getLegLabel = (fromType, toType) => {
    if (!fromType || !toType) return null;
    if (fromType === "dropoff" && toType === "pickup") {
      return t("empty_run");
    }
    return t("transportfahrt");
  };

  const renderLegsInfo = () => {
    // ... (نفس الكود السابق)
    // اختصاراً، لم يتغير هذا الجزء
    if (!priceData) return null;

    const legs = [];

    if (priceData.leg_1) {
      legs.push({
        from: priceData.leg_1.from,
        to: priceData.leg_1.to,
        distance: priceData.leg_1.distance,
        duration: priceData.duration1_text,
        price: priceData.leg_1.price,
        type: "direct",
        label:
          getLegLabel(priceData.leg_1.from_type, priceData.leg_1.to_type) ??
          t("route_1"),
        icon: <FaRoute className="text-primary" />,
      });
    }

    if (priceData.method_2_legs && Array.isArray(priceData.method_2_legs)) {
      priceData.method_2_legs.forEach((leg, index) => {
        const from = leg.from || priceData.booking_from?.[index] || "N/A";
        const to = leg.to || priceData.booking_to?.[index + 1] || "N/A";
        const distance = leg.distance || 0;
        const duration = priceData.duration2_text?.[index] || "N/A";
        
        legs.push({
          from: from,
          to: to,
          distance: distance,
          duration: duration,
          price: leg.price,
          discount: leg.discount_percentage,
          type: "leg",
          label: getLegLabel(leg.from_type, leg.to_type) ?? t("route_leg"),
          icon: <FaRoute className="text-success" />,
        });
      });
    }

    if (legs.length === 0 && priceData.booking_from && priceData.booking_to) {
      const fromAddresses = Array.isArray(priceData.booking_from) 
        ? priceData.booking_from 
        : [priceData.booking_from];
      const toAddresses = Array.isArray(priceData.booking_to) 
        ? priceData.booking_to 
        : [priceData.booking_to];
      
      if (fromAddresses.length > 0 && toAddresses.length > 0) {
        legs.push({
          from: fromAddresses[0],
          to: toAddresses[toAddresses.length - 1],
          distance: priceData.total_distance_km || "N/A",
          duration: priceData.total_duration || "N/A",
          type: "direct",
          label: t("route_total"),
          icon: <FaRoute className="text-primary" />,
        });
      }
    }

    return (
      <div className="legs-info mt-4">
        <h6 className="fw-bold mb-3">
          <FaRoad className="me-2 text-orange" />
          {t("route_details")}
        </h6>
        
        {priceData.total_distance_km && (
          <div className="total-route-info mb-3 p-3 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <span className="text-muted small">{t("distance")}</span>
                <div className="fw-bold fs-5 text-primary">
                  {priceData.total_distance_km} Km
                </div>
              </div>
              <div>
                <span className="text-muted small">{t("duration")}</span>
                <div className="fw-bold fs-5 text-success">
                  {priceData.total_duration}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {legs.map((leg, index) => (
          <div key={`leg-${index}`} className="d-flex align-items-center flex-wrap gap-3 w-100 legs-info-item">
            <span className="leg-number bg-orange text-white rounded-circle d-flex align-items-center justify-content-center">
              {index + 1}
            </span>
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-bold">{leg.label}</span>
                <span className="text-muted small">•</span>
                <span className="text-muted small">
                  <FaMapMarkerAlt className="me-1 text-success" size={12} />
                  {leg.from}
                </span>
                <span className="text-muted small">→</span>
                <span className="text-muted small">
                  <FaMapMarkerAlt className="me-1 text-danger" size={12} />
                  {leg.to}
                </span>
              </div>
            </div>
            <Badge bg="light" text="dark" className="fw-bold">
              {leg.distance} km
            </Badge>
          </div>
        ))}

        {/* <Accordion defaultActiveKey="0" className="legs-accordion">
          {legs.map((leg, index) => (
            <Accordion.Item eventKey={String(index)} key={index}>
              <Accordion.Header>
                <div className="d-flex align-items-center flex-wrap gap-3 w-100">
                  <span className="leg-number bg-orange text-white rounded-circle d-flex align-items-center justify-content-center">
                    {index + 1}
                  </span>
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span className="fw-bold">{leg.label}</span>
                      <span className="text-muted small">•</span>
                      <span className="text-muted small">
                        <FaMapMarkerAlt className="me-1 text-success" size={12} />
                        {leg.from}
                      </span>
                      <span className="text-muted small">→</span>
                      <span className="text-muted small">
                        <FaMapMarkerAlt className="me-1 text-danger" size={12} />
                        {leg.to}
                      </span>
                    </div>
                  </div>
                  <Badge bg="light" text="dark" className="fw-bold">
                    {leg.distance} km
                  </Badge>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col xs={12} sm={6}>
                    <div className="d-flex align-items-center gap-2 p-2">
                      <FaRoad className="text-primary" />
                      <div>
                        <div className="small text-muted">{t("distance")}</div>
                        <div className="fw-bold">{leg.distance} km</div>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} sm={6}>
                    <div className="d-flex align-items-center gap-2 p-2">
                      <FaClock className="text-success" />
                      <div>
                        <div className="small text-muted">{t("duration")}</div>
                        <div className="fw-bold">{leg.duration}</div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion> */}
      </div>
    );
  };

  // دالة لتحديث الإحداثيات من الـ response
  const updateCoordinatesFromResponse = (responseData) => {
    if (!responseData) return;

    try {
      let fromCoords = null;
      let toCoords = null;

      if (
        responseData.from_lat_long &&
        Array.isArray(responseData.from_lat_long) &&
        responseData.from_lat_long.length > 0
      ) {
        const firstFromCoord = responseData.from_lat_long[0];
        if (Array.isArray(firstFromCoord) && firstFromCoord.length > 0) {
          const coordString = firstFromCoord[0];
          if (coordString && typeof coordString === "string") {
            const [lat, lng] = coordString
              .split(",")
              .map((coord) => parseFloat(coord.trim()));
            fromCoords = [lat, lng];
          }
        }
      }

      if (
        responseData.to_lat_long &&
        Array.isArray(responseData.to_lat_long) &&
        responseData.to_lat_long.length > 0
      ) {
        const firstToCoord = responseData.to_lat_long[0];
        if (Array.isArray(firstToCoord) && firstToCoord.length > 0) {
          const coordString = firstToCoord[0];
          if (coordString && typeof coordString === "string") {
            const [lat, lng] = coordString
              .split(",")
              .map((coord) => parseFloat(coord.trim()));
            toCoords = [lat, lng];
          }
        }
      }

      console.log("📍 Updated coordinates from response:", {
        from: fromCoords,
        to: toCoords,
      });
      setCoordinates({
        from: fromCoords,
        to: toCoords,
      });
    } catch (error) {
      console.error("❌ Error parsing coordinates from response:", error);
      setCoordinates({ from: null, to: null });
    }
  };

  const handleAddZustellung = () => {
    setZustellungFields([...zustellungFields, ""]);
    setFormData((prev) => ({
      ...prev,
      zustellung: [
        ...prev.zustellung,
        { address: "", lat: null, lng: null, order: orderCounterRef.current++ },
      ],
    }));
    setLocationsChanged((prev) => prev + 1);
  };

  const handleAddAbholung = () => {
    setAbholungFields([...abholungFields, ""]);
    setFormData((prev) => ({
      ...prev,
      abholung: [
        ...prev.abholung,
        { address: "", lat: null, lng: null, order: orderCounterRef.current++ },
      ],
    }));
    setLocationsChanged((prev) => prev + 1);
  };

  const handleLocationChange = (e) => {
    const { name, value, lat, lng } = e.target;
    const match = name.match(/([a-zA-Z]+)\[(\d+)\]/);
    if (!match) return;

    const [_, fieldName, indexStr] = match;
    const index = parseInt(indexStr, 10);
    const hasSelectedCoordinates =
      lat !== undefined && lat !== null && lng !== undefined && lng !== null;

    setFormData((prev) => {
      const updated = { ...prev };
      updated[fieldName] = [...(prev[fieldName] || [])];
      const existing = prev[fieldName]?.[index];
      updated[fieldName][index] = {
        address: value,
        lat: hasSelectedCoordinates ? lat : null,
        lng: hasSelectedCoordinates ? lng : null,
        order: existing?.order ?? orderCounterRef.current++,
      };
      return updated;
    });

    setError("");
  };

  // دالة لحذف موقع بالكامل (مسح العنوان والإحداثيات)
  const clearLocation = (fieldName, index) => {
    setFormData((prev) => {
      const updated = { ...prev };
      updated[fieldName] = [...(prev[fieldName] || [])];
      updated[fieldName][index] = {
        address: "",
        lat: null,
        lng: null,
        order: updated[fieldName][index]?.order ?? orderCounterRef.current++,
      };
      return updated;
    });
    // نزيد locationsChanged لتفعيل إعادة الحساب (لكن الحساب لن يتم لأن الإحداثيات أصبحت null)
    setLocationsChanged((prev) => prev + 1);
    setError("");
  };

  /**
   * ✅ بيبني مصفوفة "stops" مرتبة بنفس ترتيب إدخال العميل الحقيقي
   */
  const getOrderedStops = () => {
    const pickups = formData.abholung
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item?.address?.trim())
      .map(({ item, idx }) => ({
        type: "pickup",
        address: item.address.trim(),
        lat: item.lat,
        lng: item.lng,
        order: item.order ?? idx,
      }));

    const drops = formData.zustellung
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item?.address?.trim())
      .map(({ item, idx }) => ({
        type: "dropoff",
        address: item.address.trim(),
        lat: item.lat,
        lng: item.lng,
        order: (item.order ?? idx) + pickups.length,
      }));

    return [...pickups, ...drops]
      .sort((a, b) => a.order - b.order)
      .map(({ type, address, lat, lng }) => ({ type, address, lat, lng }));
  };

  const removeLocation = (fieldName, index) => {
    if (fieldName === "abholung") {
      const updated = [...abholungFields];
      updated.splice(index, 1);
      setAbholungFields(updated);
    } else {
      const updated = [...zustellungFields];
      updated.splice(index, 1);
      setZustellungFields(updated);
    }

    setFormData((prev) => {
      const copy = { ...prev };
      copy[fieldName].splice(index, 1);
      return copy;
    });

    setError("");
    setLocationsChanged((prev) => prev + 1);
  };

  const handleVehicleSelect = (vehicleId) => {
    if (!error) {
      setSelectedVehicle(vehicleId);
      if (vehicleId !== 4) {
        setNeedsHebebuehne(null);
      }
    }
  };

  const onSubmit = async () => {
    if (error) {
      alert(t("fix_errors_before_continue"));
      return;
    }

    if (!selectedVehicle) {
      alert(t("please_select_vehicle"));
      return;
    }

    if (selectedVehicle === 4 && needsHebebuehne === null) {
      alert(t("please_answer_hebebuehne"));
      return;
    }

    const validAbholung = formData.abholung.filter((item) =>
      item?.address?.trim(),
    );
    const validZustellung = formData.zustellung.filter((item) =>
      item?.address?.trim(),
    );

    if (validAbholung.length === 0 || validZustellung.length === 0) {
      setError(t("invalid_addresses"));
      return;
    }

    setLoading(true);

    const stops = getOrderedStops();

    const payload = {
      vehicle_id: selectedVehicle,
      stops,
      booking_from: stops
        .filter((s) => s.type === "pickup")
        .map((s) => s.address),
      booking_to: stops
        .filter((s) => s.type === "dropoff")
        .map((s) => s.address),
      crane: selectedVehicle === 4 ? (needsHebebuehne ? "yes" : "no") : "no",
      extra_service: [
        ...(extraServices.loading ? ["loading"] : []),
        ...(extraServices.unloading ? ["unloading"] : []),
      ],
    };

    console.log("🚀 Sending data:", payload);

    try {
      const res = await userAPI.bookings.calculateVehicles(payload);
      if (res.success) {
        setCalculatedData(res.data);
        console.log("✅ Calculated data:", res.data);

        const selectedVehicleData = vehicles.find(
          (v) => v.id === selectedVehicle,
        );
        const calculatedPriceData = res.data?.data || res.data;
        const selectedVehiclePrice = calculatedPriceData?.vehicles?.find(
          (v) => v.vehicle_id === selectedVehicle,
        );

        const allDataToSend = {
          formData: {
            abholung: formData.abholung,
            zustellung: formData.zustellung,
            selectedVehicle: selectedVehicle,
          },
          calculatedData: calculatedPriceData,
          selectedVehicleInfo: {
            ...selectedVehicleData,
            calculatedPrice: selectedVehiclePrice,
            weight: selectedVehicleData.weight,
            length: selectedVehicleData.length,
            width: selectedVehicleData.width,
            height: selectedVehicleData.height,
            max_shipments: selectedVehicleData.max_shipments,
            crane:
              selectedVehicle === 4 ? (needsHebebuehne ? "yes" : "no") : "no",
            needs_hebebuehne: selectedVehicle === 4 ? needsHebebuehne : null,
          },
          allVehicles: vehicles,
          allVehiclesPrices: calculatedPriceData?.vehicles || [],
          summary: {
            distance:
              calculatedPriceData?.total_distance_km ||
              calculatedPriceData?.distance_km,
            duration:
              calculatedPriceData?.total_duration ||
              calculatedPriceData?.duration_text,
            subTotal: selectedVehiclePrice?.sub_total,
            total: selectedVehiclePrice?.total,
            fromAddresses: validAbholung.map((item) =>
              typeof item === "string" ? item : item.address,
            ),
            toAddresses: validZustellung.map((item) =>
              typeof item === "string" ? item : item.address,
            ),
          },
        };

        navigate("/bookings/steps", {
          state: allDataToSend,
        });
      } else {
        setError(res.message || t("error_calculating_vehicles"));
        alert(res.message || t("error_calculating_vehicles"));
      }
    } catch (err) {
      console.error("❌ API Error:", err);
      const errorMessage = translateBackendMessage(
        err?.response?.data || err,
        t("unknown_error"),
      );
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedVehiclePrice = () => {
    if (
      !priceData ||
      !priceData.vehicles ||
      !Array.isArray(priceData.vehicles) ||
      !selectedVehicle
    ) {
      return null;
    }

    const vehicle = priceData.vehicles.find(
      (v) => v.vehicle_id === selectedVehicle,
    );
    console.log("Selected Vehicle Price:", vehicle);
    return vehicle
      ? {
        sub_total: vehicle.sub_total,
        total: vehicle.total,
      }
      : null;
  };

  const getVehiclePrice = (vehicleId) => {
    if (
      !priceData ||
      !priceData.vehicles ||
      !Array.isArray(priceData.vehicles)
    ) {
      return null;
    }

    const vehicle = priceData.vehicles.find((v) => v.vehicle_id === vehicleId);
    console.log("Vehicle Price:", vehicle);
    return vehicle ? `${vehicle.sub_total} EUR` : null;
  };

  const canProceed =
    selectedVehicle &&
    priceData &&
    !error &&
    !calculatingPrice &&
    (selectedVehicle !== 4 || needsHebebuehne !== null);

  const selectedPrice = getSelectedVehiclePrice();
  const orderedRouteStops = getOrderedStops()
    .filter((stop) => stop.lat !== null && stop.lng !== null && stop.lat !== undefined && stop.lng !== undefined)
    .map((stop) => [Number(stop.lat), Number(stop.lng)]);

  return (
    <>
      <SEO
        title="TransCirox – Transport buchen | Umzug & Transport jetzt anfragen"
        description="Buchen Sie schnell und einfach Ihren Transport oder Umzug bei TransCirox. Vergleichen Sie Angebote und wählen Sie den besten Transportpartner."
        keywords="Transport buchen, Umzug buchen, Transportangebot, Umzug anfragen, Gütertransport buchen, Möbeltransport buchen"
      />
      <NavbarMenu />
      <Container fluid className="order-page p-5">
        <Container fluid>
          <h2 className="page-title" data-aos="fade-down">
            {t("place_order")}
          </h2>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="position-relative z-3 mb-4" data-aos="fade-right">
              <Col md={6} className="mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <div className="booking-icon">
                        <FaLocationDot />
                      </div>
                      <h5 className="mb-0 fw-bold">
                        {t("pickup")}
                        {calculatingPrice && (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="ms-2"
                          />
                        )}
                      </h5>
                    </div>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={handleAddAbholung}
                      disabled={calculatingPrice}
                    >
                      ➕ {t("add_location")}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {abholungFields.map((_, index) => (
                      <div
                        className="d-flex align-items-center mb-3"
                        key={index}
                      >
                        <Form.Group
                          controlId={`abholung-${index}`}
                          className="flex-grow-1"
                        >
                          <Form.Label className="small fw-bold mb-1">
                            {t("pickup_address_label")} {(index !== 0 &&  index + 1)}
                            {calculatingPrice && index === 0 && (
                              <span className="text-warning ms-1">
                                ({t("updating_prices")})
                              </span>
                            )}
                          </Form.Label>
                          <InputGroup className="booking-input-group">
                            <LocationAutocomplete
                              name={`abholung[${index}]`}
                              value={formData.abholung[index]?.address || ""}
                              onChange={handleLocationChange}
                              className="form-control-sm booking-location"
                              placeholder={t("enter_address")}
                              disabled={calculatingPrice}
                              wrapperClassName="flex-grow-1"
                            />
                            {formData.abholung[index]?.address && (
                              <Button
                                variant="link"
                                className="btn-clear"
                                onClick={() => clearLocation("abholung", index)}
                                disabled={calculatingPrice}
                                aria-label="Clear address"
                              >
                                <FaTimes />
                              </Button>
                            )}
                          </InputGroup>
                        </Form.Group>
                        {index > 0 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="ms-2 mt-4"
                            onClick={() => removeLocation("abholung", index)}
                            disabled={calculatingPrice}
                          >
                            <MdDelete />
                          </Button>
                        )}
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6} className="mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <div className="booking-icon">
                        <FaLocationDot />
                      </div>
                      <h5 className="mb-0 fw-bold">
                        {t("delivery")}
                        {calculatingPrice && (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="ms-2"
                          />
                        )}
                      </h5>
                    </div>
                    <Button
                      variant="light"
                      size="sm"
                      onClick={handleAddZustellung}
                      disabled={calculatingPrice}
                    >
                      ➕ {t("add_location")}
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {zustellungFields.map((_, index) => (
                      <div
                        className="d-flex align-items-center mb-3"
                        key={index}
                      >
                        <Form.Group
                          controlId={`zustellung-${index}`}
                          className="flex-grow-1"
                        >
                          <Form.Label className="small fw-bold mb-1">
                            {t("delivery_address_label")} {(index !== 0 &&  index + 1)}
                            {calculatingPrice && index === 0 && (
                              <span className="text-warning ms-1">
                                ({t("updating_prices")})
                              </span>
                            )}
                          </Form.Label>
                          <InputGroup className="booking-input-group">
                            <LocationAutocomplete
                              name={`zustellung[${index}]`}
                              value={formData.zustellung[index]?.address || ""}
                              onChange={handleLocationChange}
                              className="form-control-sm booking-location"
                              placeholder={t("enter_address")}
                              disabled={calculatingPrice}
                              wrapperClassName="flex-grow-1"
                            />
                            {formData.zustellung[index]?.address && (
                              <Button
                                variant="link"
                                className="btn-clear"
                                onClick={() => clearLocation("zustellung", index)}
                                disabled={calculatingPrice}
                                aria-label="Clear address"
                              >
                                <FaTimes />
                              </Button>
                            )}
                          </InputGroup>
                        </Form.Group>
                        {index > 0 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="ms-2 mt-"
                            onClick={() => removeLocation("zustellung", index)}
                            disabled={calculatingPrice}
                          >
                            <MdDelete />
                          </Button>
                        )}
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {error && (
              <Row className="mb-4" data-aos="fade-up">
                <Col xs={12}>
                  <Alert variant="danger" className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <div>
                      <strong>{t("error")}:</strong> {t("error_calculating_price")}
                    </div>
                  </Alert>
                </Col>
              </Row>
            )}

            <Row className="justify-content-center mt-3" data-aos="fade-up">
              <Col xs={12} lg={6}>
                <h4 className="section-title mb-4">
                  {t("vehicle_selection")}
                  {calculatingPrice && (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  )}
                </h4>

                {fetchingVehicles ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" />
                    <p className="mt-2">{t("loading_vehicles")}</p>
                  </div>
                ) : vehicles.length > 0 ? (
                  <Row>
                    {vehicles.map((vehicle, index) => {
                      const vehiclePrice = getVehiclePrice(vehicle.id);
                      const isActive = vehicle.is_active === 1 || vehicle.is_active === "1";
                      const isSelected = selectedVehicle === vehicle.id;

                      return (
                        <Col xs={12} key={vehicle.id} className="mb-3">
                          <Card
                            className={`vehicle-card shadow-sm ${
                              isSelected && isActive ? "selected border-primary" : ""
                            } ${error || calculatingPrice || !isActive ? "opacity-50" : ""}`}
                            onClick={() => {
                              if (!error && !calculatingPrice && isActive) {
                                handleVehicleSelect(vehicle.id);
                              }
                            }}
                            style={{
                              cursor: !isActive || error || calculatingPrice ? "not-allowed" : "pointer",
                              position: "relative",
                            }}
                          >
                            <Row className="g-0 align-items-center">
                              <Col xs={12} md={5}>
                                <Card.Img
                                  src={
                                    vehicleImages[index] || vehicle.image || car
                                  }
                                  alt={vehicle.name}
                                  className="vehicle-image img-fluid"
                                />

                                {!isActive && (
                                  <div className="vehicle-inactive-overlay">
                                    <Badge 
                                      bg="danger" 
                                      className="px-3 py-2"
                                      style={{ 
                                        fontSize: "0.75rem",
                                        position: "absolute",
                                        top: "12px",
                                        left: "12px",
                                        zIndex: 5,
                                        boxShadow: "0 2px 8px rgba(220, 53, 69, 0.4)",
                                      }}
                                    >
                                      <FaExclamationTriangle className="me-1" size={12} />
                                      {t("vehicle_inactive")}
                                    </Badge>
                                  </div>
                                )}
                              </Col>
                              <Col xs={12} md={7}>
                                <Card.Body className="position-relative">
                                  <Card.Subtitle className="mb-2 fw-bold">
                                    {vehiclePrice ||
                                      (error
                                        ? t("price_not_available")
                                        : calculatingPrice
                                          ? t("updating_prices")
                                          : !vehicle.is_active
                                            ? t("unavailable")
                                            : t("updating_prices"))}
                                  </Card.Subtitle>
                                  <Card.Text className="small mb-1">
                                    {vehicle.size || vehicle.vehicle_size}
                                  </Card.Text>
                                  {selectedVehicle === vehicle.id &&
                                    !error &&
                                    !calculatingPrice && (
                                      <FaCheckCircle
                                        size={20}
                                        className="vehicle-check"
                                      />
                                    )}
                                </Card.Body>
                              </Col>
                            </Row>
                          </Card>
                        </Col>
                      );
                    })}

                    <Col xs={12}>
                      {selectedVehicle === 4 && (
                        <Row className="mb-4" data-aos="fade-up">
                          <Col xs={12}>
                            <Card className="border-warning">
                              <Card.Header className="bg-warning text-dark">
                                <h5 className="mb-0">
                                  {t("needs_hebebuehne")}
                                </h5>
                              </Card.Header>
                              <Card.Body>
                                <Form.Group>
                                  <div className="d-flex gap-3">
                                    <Button
                                      variant={needsHebebuehne === true ? "success" : "outline-success"}
                                      onClick={() => {
                                        if (!error && !calculatingPrice) {
                                          setNeedsHebebuehne(true);
                                        }
                                      }}
                                      className="flex-fill"
                                      disabled={!selectedVehicle || calculatingPrice}
                                    >
                                      ✅ {t("yes")}
                                    </Button>
                                    <Button
                                      variant={
                                        needsHebebuehne === false
                                          ? "danger"
                                          : "outline-danger"
                                      }
                                      onClick={() => {
                                        if (!error && !calculatingPrice) {
                                          setNeedsHebebuehne(false);
                                        }
                                      }}
                                      className="flex-fill"
                                    >
                                      ❌ {t("no")}
                                    </Button>
                                  </div>
                                  {needsHebebuehne !== null && (
                                    <div className="mt-2 text-muted small">
                                      {t("your_selection")}:{" "}
                                      <strong>
                                        {needsHebebuehne ? t("yes") : t("no")}
                                      </strong>
                                    </div>
                                  )}
                                </Form.Group>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      )}
                    </Col>
                  </Row>
                ) : (
                  <p className="text-center mt-4">{t("no_vehicles_found")}</p>
                )}
              </Col>

              <Col xs={12} lg={6}>
                <h5 className="section-title mb-3">{t("delivery_route")}</h5>
                <Card className="map-card shadow-sm" data-aos="fade-left">
                  <MultiLocationMap
                    routeStops={orderedRouteStops}
                    fromCoords={priceData?.from_lat_long || []}
                    toCoords={priceData?.to_lat_long || []}
                    key={`${JSON.stringify(orderedRouteStops)}-${JSON.stringify(priceData?.from_lat_long)}-${JSON.stringify(priceData?.to_lat_long)}`}
                  />
                </Card>

                {priceData && renderLegsInfo()}
              </Col>
            </Row>

            <Row>
              {selectedPrice && (
                <div className="mt-3">
                  <span>{t("preis")}</span>
                  <div>
                    <h2 className="my-0">
                      {selectedPrice.sub_total} EUR Netto
                    </h2>
                  </div>
                  <div>
                    {t("plus_vat", { vatRate: 19 })} {selectedPrice.total} EUR
                  </div>
                </div>
              )}
            </Row>

            <Row className="mt-4">
              <Col className="text-center">
                <Button
                  type="submit"
                  size="lg"
                  className="submit-btn px-5"
                  disabled={loading || !canProceed || calculatingPrice}
                  variant={
                    canProceed && !calculatingPrice ? "primary" : "secondary"
                  }
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t("calculating")}
                    </>
                  ) : calculatingPrice ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t("updating_prices")}
                    </>
                  ) : (
                    t("proceed_to_shipping")
                  )}
                </Button>

                {!canProceed && !loading && (
                  <div className="mt-2 text-muted small">
                    {calculatingPrice && t("wait_for_price_update")}
                    {!calculatingPrice &&
                      !selectedVehicle &&
                      t("please_select_vehicle")}
                    {!calculatingPrice &&
                      selectedVehicle === 4 &&
                      needsHebebuehne === null &&
                      t("please_answer_hebebuehne")}
                    {!calculatingPrice &&
                      selectedVehicle &&
                      selectedVehicle !== 4 &&
                      error &&
                      t("fix_errors_above")}
                    {!calculatingPrice &&
                      selectedVehicle &&
                      !error &&
                      !priceData &&
                      t("wait_for_price_calculation")}
                  </div>
                )}
              </Col>
            </Row>
          </Form>
        </Container>
      </Container>
      <Footer />

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}